import { memo, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isEmpty, isNil, values } from 'lodash';
// Import Types
import type { TemplateById } from 'types';
import type {
    CJTemplateMessageConfigProps,
    CJTemplateMessageConfigInfoBlockItemProps,
    DynamicVariablesDataItem,
    ProviderData,
} from './types';
// Import Hooks
import useRequest from 'hooks/UseRequest';
import useCancelableRequests from 'hooks/UseCancelableRequests';
// Import Services
import { CustomerJourneyHttpService, TemplateHttpService, UtilsHttpService } from 'services/http';
// Import Components
import { CJInfoBlockLabelValueWrapper, CJReferenceInArgumentInfoBlock } from 'components/CustomerJourney/infoBlocks';
import TemplateInfoModal from 'components/TemplateModal/TemplateInfoModal';
// Import Constants
import { DeliveryMethodTypes, l } from 'constants/common';

const CJTemplateMessageConfigInfoBlockItem: React.FC<CJTemplateMessageConfigInfoBlockItemProps> = ({
    data,
    elements,
    option,
    blockId,
}) => {
    const { t } = useTranslation();
    const { doGetRequest, doPostRequest } = useRequest();

    const [isTemplateLoading, setIsTemplateLoading] = useState(false);
    const [templateData, setTemplateData] = useState<TemplateById>();
    const [dynamicVariablesData, setDynamicVariablesData] = useState<DynamicVariablesDataItem[]>([]);
    const [isVisibleTemplateInfo, setIsVisibleTemplateInfo] = useState(false);

    const [isProviderLoading, setIsProviderLoading] = useState(false);
    const [providerData, setProviderData] = useState<ProviderData | null>(null);

    const { getTemplate, getTemplateModels, getPartnerMessageProviders } = useCancelableRequests(
        {
            getTemplate: TemplateHttpService.getTemplate,
            getTemplateModels: CustomerJourneyHttpService.getTemplateModels,
            getPartnerMessageProviders: UtilsHttpService.getPartnerMessageProviders,
        },
        { takeLatest: ['getTemplate', 'getTemplateModels', 'getPartnerMessageProviders'] },
    );

    const openTemplateModal = useCallback(() => {
        setIsVisibleTemplateInfo(true);
    }, []);

    useEffect(() => {
        const templateId = data.template?.value;
        if (!isNil(templateId)) {
            setIsTemplateLoading(true);
            doGetRequest(getTemplate.request, {
                queryString: { templateId },
                successCallback: (data) => {
                    setIsTemplateLoading(false);
                    setTemplateData(data);
                },
            });
            if (!isEmpty(data?.dynamicVariables)) {
                doGetRequest(getTemplateModels.request, {
                    queryString: { templateId },
                    successCallback: (responseData) => {
                        const fieldNameMap = responseData.reduce((acc, val) => {
                            acc[val.FieldName] = val;
                            return acc;
                        }, {});

                        setDynamicVariablesData(
                            values(data.dynamicVariables).map(({ fieldName, value }) => {
                                const isTranslatable = !isNil(fieldNameMap[fieldName]?.WfType);
                                return {
                                    value: value,
                                    fieldName: isTranslatable ? fieldNameMap[fieldName].DisplayNameKey : fieldName,
                                    isTranslatable: isTranslatable,
                                };
                            }),
                        );
                    },
                });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data?.template?.value]);

    // Provider Part
    useEffect(() => {
        setIsProviderLoading(true);
        doPostRequest(getPartnerMessageProviders.request, {
            requestBody: { DeliveryMethod: data.deliveryMethod },
            successCallback: (Data) => {
                setIsProviderLoading(false);
                setProviderData(
                    Data.find((item) => {
                        return item.Id === data?.provider?.value;
                    }),
                );
            },
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data?.deliveryMethod]);

    return (
        <>
            <CJInfoBlockLabelValueWrapper label={t(l.Id)} value={data?.template?.value} />

            <CJInfoBlockLabelValueWrapper
                label={t(l.DeliveryMethod)}
                value={t(l[DeliveryMethodTypes[data.deliveryMethod]])}
                isLoading={isProviderLoading}
            />

            <CJInfoBlockLabelValueWrapper
                label={t(l.TemplateName)}
                value={templateData?.Name}
                isLoading={isTemplateLoading}
                onClick={openTemplateModal}
                isClickable={true}
            />

            <CJInfoBlockLabelValueWrapper
                label={t(l.ProviderName)}
                value={providerData?.Name}
                isLoading={isProviderLoading}
            />

            {isVisibleTemplateInfo && (
                <TemplateInfoModal
                    templateId={templateData?.TemplateId}
                    handleClose={() => setIsVisibleTemplateInfo(false)}
                    isVisible={isVisibleTemplateInfo}
                    isPredefined={false}
                    isGlobal={false}
                />
            )}

            {dynamicVariablesData.map(({ fieldName, value, isTranslatable }, index) => {
                return (
                    <div key={`${data?.template?.value}_${index}`} className="cj-info-block-dynamic-variable-container">
                        <div className="cj-dynamic-variable-field-name">
                            {isTranslatable ? t(fieldName) : fieldName}
                        </div>
                        <CJReferenceInArgumentInfoBlock
                            data={value}
                            elements={elements}
                            option={option}
                            blockId={blockId}
                        />
                    </div>
                );
            })}
        </>
    );
};

const CJTemplateMessageConfigInfoBlock: React.FC<CJTemplateMessageConfigProps> = ({
    data,
    elements,
    option,
    blockId,
}) => {
    return (
        <div>
            {values(data).map((deliveryMethodData) => {
                const { deliveryMethod } = deliveryMethodData;
                return (
                    <div key={deliveryMethod} className="best-channel-time-info-block-item">
                        <CJTemplateMessageConfigInfoBlockItem
                            data={deliveryMethodData}
                            elements={elements}
                            option={option}
                            blockId={blockId}
                        />
                    </div>
                );
            })}
        </div>
    );
};

export default memo(CJTemplateMessageConfigInfoBlock);
